import React from "react";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import Form, { SIZE, TYPE } from "components/Form";
import { margins } from "style";
import { MUTATION_TYPE, useToast } from "hooks";
import { UPDATE_TEAM } from "graphql/mutations";
import { USER_TYPES } from "constants/index";
import { toUcFirst } from "utils";

const ALLOWED_ROLES = [
  { id: USER_TYPES.client, name: "Client" },
  { id: USER_TYPES.freelancer, name: "Freelancer" },
];

/**
 * GeneralSettingsForm
 *
 * @param {Object}    data
 */
const GeneralSettingsForm = ({ data }) => {
  const { toast } = useToast();

  const handleComplete = () =>
    toast.success(<FormattedMessage id="Squads.Squad.Settings.GeneralSettings.UpdateSuccess" />);
  const { featureVisibility } = data;
  const { dailyReport, effortReport, engineeringMetrics } = featureVisibility || {};
  const initialValues = {
    featureVisibility: {
      dailyReport: dailyReport?.map((item) => ({ id: item, name: toUcFirst(item.toLowerCase()) })),
      effortReport: effortReport?.map((item) => ({ id: item, name: toUcFirst(item.toLowerCase()) })),
      engineeringMetrics: engineeringMetrics?.map((item) => ({ id: item, name: toUcFirst(item.toLowerCase()) })),
    },
  };

  const formProps = {
    initialValues,
    mutationType: MUTATION_TYPE.update,
    mutationData: {
      mutationName: {
        update: UPDATE_TEAM,
      },
    },
    numColumns: 4,
    saveButton: {
      show: true,
      props: {
        css: styles.updateButton,
      },
    },
    parentKeyValue: data?.id,
    updateId: data?.id,
    onCompleted: handleComplete,
    data: FORM_DATA,
  };

  return <Form {...formProps} />;
};

const styles = {
  updateButton: css`
    margin-bottom: ${margins.xsmall};
  `,
};

const FORM_DATA = [
  {
    style: css`
      width: 100%;
    `,
    items: [
      {
        label: <FormattedMessage id="Squads.Squad.Settings.GeneralSettings.Form.Title" />,
        type: TYPE.h4,
        size: SIZE.xlarge,
      },
      {
        type: TYPE.label,
        size: SIZE.xlarge,
      },
      {
        label: <FormattedMessage id="Squads.Squad.Settings.GeneralSettings.Form.EffortReport.Label" />,
        type: TYPE.label,
        size: SIZE.small,
      },
      {
        type: TYPE.tags,
        size: SIZE.small,
        parentObjectKey: "featureVisibility",
        properties: {
          name: "effortReport",
          options: ALLOWED_ROLES,
          noOptionsMessage: "No Roles found",
          minLength: 0,
          placeholder: "Select roles...",
        },
      },
      {
        type: TYPE.label,
        size: SIZE.medium,
      },
      {
        label: <FormattedMessage id="Squads.Squad.Settings.GeneralSettings.Form.EngineeringMetrics.Label" />,
        type: TYPE.label,
        size: SIZE.small,
      },
      {
        type: TYPE.tags,
        size: SIZE.small,
        parentObjectKey: "featureVisibility",
        properties: {
          name: "engineeringMetrics",
          options: ALLOWED_ROLES,
          noOptionsMessage: "No Roles found",
          minLength: 0,
          placeholder: "Select roles...",
        },
      },
      {
        type: TYPE.label,
        size: SIZE.medium,
      },
      {
        label: <FormattedMessage id="Squads.Squad.Settings.GeneralSettings.Form.DailyReport.Label" />,
        type: TYPE.label,
        size: SIZE.small,
      },
      {
        type: TYPE.tags,
        size: SIZE.small,
        parentObjectKey: "featureVisibility",
        properties: {
          name: "dailyReport",
          options: ALLOWED_ROLES,
          noOptionsMessage: "No Roles found",
          minLength: 0,
          placeholder: "Select roles...",
        },
      },
    ],
  },
];

GeneralSettingsForm.defaultProps = {
  data: {},
  refetch: () => {},
};

GeneralSettingsForm.propTypes = {
  data: PropTypes.object,
  refetch: PropTypes.func,
};

export default GeneralSettingsForm;
